if (!customElements.get('price-range')) {
  class PriceRange extends HTMLElement {
    constructor() {
      super();
      window.initLazyScript(this, this.init.bind(this));
    }

    init() {
      this.minNumberInput = document.getElementById('price-range-number-min');
      this.maxNumberInput = document.getElementById('price-range-number-max');
      this.minSliderInput = document.getElementById('price-range-slider-min');
      this.maxSliderInput = document.getElementById('price-range-slider-max');

      // TUFF-TREK: hidden inputs (#price-range-excl-min/max) carry the EX-VAT
      // values Shopify filters against; the visible inputs show VAT-inclusive
      // amounts. updateExclInputs() re-reads these from the DOM each call.

      this.minValue = Number(this.minNumberInput.min);
      this.maxValue = Number(this.maxNumberInput.max);
      this.timeout = null;
      this.addEventListener('input', this.handleInput.bind(this));

      // Note: the optional "Apply" button is handled by facet-filters.js via
      // delegated click (its listeners bind immediately, not lazily), so the
      // button works even before this component's lazy init has run. It calls
      // updateExclInputs() on this element, which is why that method below is
      // written to be self-contained (it re-reads the DOM each call).

      // Make sure hidden ex-VAT inputs reflect the initial state.
      this.updateExclInputs();
    }

    /**
     * Handles 'input' events on the price range component.
     * @param {object} evt - Event object.
     */
    handleInput(evt) {
      if (evt.detail !== undefined) {
        clearTimeout(this.timeout);

        this.timeout = setTimeout(() => {
          this.updateSliderInputs(evt);
          this.updateNumberInputs();
          this.updateExclInputs();
        }, 500);
      } else {
        this.updateSliderInputs(evt);
        this.updateNumberInputs();
        this.updateExclInputs();
      }
    }

    /**
     * Updates the value of the 'number' type inputs.
     */
    updateNumberInputs() {
      this.minNumberInput.value = this.minSliderInput.value !== this.minNumberInput.min
        ? this.minSliderInput.value : null;

      this.maxNumberInput.value = this.maxSliderInput.value !== this.maxNumberInput.max
        ? this.maxSliderInput.value : null;
    }

    /**
     * TUFF-TREK: Converts the VAT-inclusive display values into the EX-VAT
     * values Shopify's price filter expects, and writes them to the hidden
     * inputs. Runs on every change so the values are current before
     * facet-filters.js reads the form on the 'change' event.
     *
     * An empty visible value means "no bound", so the hidden input is cleared
     * (facet-filters.js strips empty params before querying).
     */
    updateExclInputs() {
      // Re-read the DOM each call so this works even if invoked before the
      // component's lazy init (e.g. from facet-filters.js when the Apply button
      // is clicked inside a dropdown that was never scrolled into view).
      const minNum = document.getElementById('price-range-number-min');
      const maxNum = document.getElementById('price-range-number-max');
      const minExcl = document.getElementById('price-range-excl-min');
      const maxExcl = document.getElementById('price-range-excl-max');
      if (!minExcl || !maxExcl || !minNum || !maxNum) return;

      const vatAdjust = this.dataset.vatAdjust === 'true';
      const vatNum = Number(this.dataset.vatNum) || 100;

      // Lower bound (gte) floors, upper bound (lte) ceils, so boundary-priced
      // products are never excluded by rounding.
      minExcl.value = PriceRange.toExcl(minNum.value, Math.floor, vatAdjust, vatNum);
      maxExcl.value = PriceRange.toExcl(maxNum.value, Math.ceil, vatAdjust, vatNum);
    }

    /**
     * Converts a VAT-inclusive display amount to its ex-VAT equivalent.
     * @param {string} displayValue - The visible (inc-VAT) input value.
     * @param {Function} round - Math.floor (min) or Math.ceil (max).
     * @param {boolean} vatAdjust - Whether VAT conversion applies.
     * @param {number} vatNum - 100 + VAT rate (e.g. 120 for 20%).
     * @returns {string} Ex-VAT whole-currency value, or '' if no bound set.
     */
    static toExcl(displayValue, round, vatAdjust, vatNum) {
      if (displayValue === '' || displayValue === null || displayValue === undefined) return '';
      const inc = parseInt(displayValue, 10);
      if (Number.isNaN(inc)) return '';
      if (!vatAdjust) return String(inc);
      return String(round((inc * 100) / vatNum));
    }

    /**
     * Updates the value of the 'range' type inputs.
     * @param {object} evt - Event object.
     */
    updateSliderInputs(evt) {
      const minValue = parseInt(this.minNumberInput.value, 10);
      const maxValue = parseInt(this.maxNumberInput.value, 10);

      if (minValue > maxValue - 10) {
        if (evt.target === this.minNumberInput) {
          this.maxSliderInput.value = minValue + 10;

          if (maxValue === this.maxValue) {
            this.minSliderInput.value = this.maxValue - 10;
          }
        } else {
          this.minSliderInput.value = maxValue - 10;
        }
      }

      if (maxValue < minValue + 10) {
        if (evt.target === this.maxNumberInput) {
          this.minSliderInput.value = maxValue - 10;

          if (minValue === this.minValue) {
            this.maxSliderInput.value = 10;
          }
        } else {
          this.maxSliderInput.value = minValue + 10;
        }
      }

      if (evt.target === this.minNumberInput) {
        this.minSliderInput.value = minValue || Number(this.minNumberInput.min);
      }

      if (evt.target === this.maxNumberInput) {
        this.maxSliderInput.value = maxValue || Number(this.maxNumberInput.max);
      }
    }
  }

  customElements.define('price-range', PriceRange);
}
